import ReactDOM from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { StyleSheetManager } from "styled-components";
import isPropValid from "@emotion/is-prop-valid";
import {
  AuthUserProvider,
  BrandProvider,
  createDefaultBrandsConfig,
  getAuthConfig,
  getAuthConfigByKey,
  InvestIndustrialIcon,
  InvestIndustrialIconDark,
  InvestIndustrialLogo,
  InvestIndustrialLogoDark,
  investIndustrialIconStacked,
  investIndustrialIconStackedDark,
  NGVIcon,
  PathwayFullLogo,
  PathwayLogo,
  registerBrandIcons,
  SessionTimeoutProvider,
  useAuth,
  useUser,
  VITE_MODE_MAPPING,
  WeBuildAiIcon,
  WeBuildAiIconStacked,
  type LogoutReason,
} from "@webuild-ai/react-auth";
import {
  clearAppSessionData,
  clearMsalCache,
  logLogoutEvent,
} from "@webuild-ai/react-auth/services";
import React, { useCallback } from "react";

import App from "./App.tsx";
import i18n, { REACT_AUTH_NAMESPACE } from "./i18n";
// Load Iconify configuration to prevent CDN/CORS issues
import "./iconify-config";
import { HeroProvider } from "./providers/HeroProvider.tsx";
import "@/styles/globals.css";
import { WeBuildClientProvider } from "./providers/WeBuildClientProvider.tsx";
import { RBACProviderWrapper } from "./providers/RBACProviderWrapper.tsx";
import { useIdpSessionHandler } from "./hooks/useIdpSessionHandler.ts";
import { ThemeProvider } from "./hooks/use-theme.tsx";
import ErrorBoundary from "./services/ErrorBoundaries.tsx";
import { TelemetryProvider, logger } from "./telemetry";

// Register brand icons BEFORE rendering - required for BrandProvider
registerBrandIcons({
  InvestIndustrialLogo,
  InvestIndustrialLogoDark,
  WeBuildAiIcon,
  NGVIcon,
  investIndustrialIconStacked,
  investIndustrialIconStackedDark,
  WeBuildAiIconStacked,
  PathwayLogo,
  PathwayFullLogo,
  InvestIndustrialIcon,
  InvestIndustrialIconDark,
});

// Get auth config at module level for BrandProvider initialBrand
// First try explicit mode mapping, then fall back to hostname-based detection
const mode = import.meta.env.MODE as string;
const configKey = VITE_MODE_MAPPING[mode];
const initialAuthConfig = configKey ? getAuthConfigByKey(configKey) : getAuthConfig();
const initialBrand = initialAuthConfig?.brand;

// Build custom brands config with env var override for NGV dashboard URL
// IMPORTANT: Pass the registered icons to createDefaultBrandsConfig to get proper icons
// (without icons, it returns PlaceholderIcon for all brands)
const ngvDashboardUrl = import.meta.env.VITE_NGV_DASHBOARD_URL as string | undefined;
const registeredIcons = {
  InvestIndustrialLogo,
  InvestIndustrialLogoDark,
  WeBuildAiIcon,
  NGVIcon,
  investIndustrialIconStacked,
  investIndustrialIconStackedDark,
  WeBuildAiIconStacked,
  PathwayLogo,
  PathwayFullLogo,
  InvestIndustrialIcon,
  InvestIndustrialIconDark,
};
const defaultNgvBrand = createDefaultBrandsConfig(registeredIcons).ngv;
const customBrands = ngvDashboardUrl
  ? {
      ngv: {
        ...defaultNgvBrand,
        dashboard: {
          type: "iframe" as const,
          url: ngvDashboardUrl,
          title: defaultNgvBrand.dashboard?.title,
          subtitle: defaultNgvBrand.dashboard?.subtitle,
          showInSideBar: defaultNgvBrand.dashboard?.showInSideBar,
          urlSegment: defaultNgvBrand.dashboard?.urlSegment,
        },
      },
    }
  : undefined;

// Wrapper component to provide navigation to AuthUserProvider
const AuthProviderWithNavigation: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const mode = import.meta.env.MODE as string;

  // Get auth config using explicit mode mapping, fall back to hostname-based detection
  // This allows a single build to work across multiple deployment targets
  const configKey = VITE_MODE_MAPPING[mode];
  const authConfig = configKey ? getAuthConfigByKey(configKey) : getAuthConfig();

  // Simple fallback if no config found (should not happen with hostname detection)
  if (!authConfig) {
    logger.error(`No auth config found for mode: ${mode}`, undefined, {
      mode,
      configKey: configKey ?? "undefined",
    });

    return <div>Configuration error for mode: {mode}</div>;
  }

  return (
    <AuthUserProvider authConfig={authConfig} onRedirectCallback={(path) => navigate(path)}>
      {children}
    </AuthUserProvider>
  );
};

/**
 * Session Timeout Wrapper - Wraps SessionTimeoutProvider with required callbacks.
 *
 * This component:
 * - Provides the logout callback for session expiry
 * - Handles IdP session expiration events from WeBuildClientProvider
 * - Performs hard logout when session expires
 *
 * NOTE: SessionTimeoutProvider (from react-auth v2.1.7+) automatically detects auth state
 * and prevents MSAL `interaction_in_progress` errors. The `useIdpSessionHandler` hook
 * still requires manual auth guards since it's Pathway-specific.
 */
const SessionTimeoutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { logout, isLoading, isAuthenticated } = useAuth();
  const { userProfile } = useUser();
  const authConfig = getAuthConfig();

  // Use i18n.t directly to get translations from the reactAuth namespace
  // This avoids useTranslation hook which can cause issues during auth redirect flows
  const t = useCallback((key: string) => i18n.t(key, { ns: REACT_AUTH_NAMESPACE }), []);

  // Guard: Don't enable session timeout management until auth is fully initialised
  // This prevents the MSAL `interaction_in_progress` error during redirect flows
  const sessionEnabled = !isLoading && isAuthenticated;

  // Handle IdP session expiration events from WeBuildClientProvider
  // Only set up the handler when authenticated to avoid race conditions
  useIdpSessionHandler({ enabled: sessionEnabled });

  // Get tenant from user profile
  const tenant = userProfile?.tenant || "default";

  /**
   * Handle session expiry by performing hard logout.
   */
  const handleSessionExpired = useCallback(
    async (reason: "absolute" | "idle" | "idp_revoked" | "manual") => {
      const logoutReason: LogoutReason =
        reason === "absolute"
          ? "session_expired"
          : reason === "idle"
            ? "idle_timeout"
            : reason === "manual"
              ? "user_requested"
              : "idp_revoked";

      logLogoutEvent(logoutReason, { provider: authConfig.provider });

      // Clear app session data first
      clearAppSessionData();

      if (authConfig.provider === "Azure") {
        // Clear MSAL cache for Azure
        clearMsalCache();
      }

      // Use the abstracted logout from useAuth which handles both Auth0 and Azure
      await logout();
    },
    [authConfig.provider, logout],
  );

  return (
    <SessionTimeoutProvider t={t} tenantKey={tenant} onSessionExpired={handleSessionExpired}>
      {children}
    </SessionTimeoutProvider>
  );
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  // <React.StrictMode>
  <ErrorBoundary>
    <BrandProvider customBrands={customBrands} initialBrand={initialBrand}>
      <ThemeProvider>
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          <HeroProvider>
            <AuthProviderWithNavigation>
              <TelemetryProvider>
                <WeBuildClientProvider>
                  <RBACProviderWrapper>
                    <SessionTimeoutWrapper>
                      <StyleSheetManager
                        //required for styled-components to work
                        shouldForwardProp={(prop, _) => isPropValid(prop)}
                      >
                        <App />
                      </StyleSheetManager>
                    </SessionTimeoutWrapper>
                  </RBACProviderWrapper>
                </WeBuildClientProvider>
              </TelemetryProvider>
            </AuthProviderWithNavigation>
          </HeroProvider>
        </BrowserRouter>
      </ThemeProvider>
    </BrandProvider>
  </ErrorBoundary>,
  // </React.StrictMode>,
);
